"use client";

import { useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { api } from "@/lib/api";
import { isTokenExpired } from "@/lib/socket-auth";

export interface VoiceEvent {
  event: string;
  data: Record<string, unknown>;
}

// Tier 0 lifecycle. Tier 1/2/3 (voice.agent.*, voice.queue.*, voice.held,
// voice.recording.available, voice.ami.*, etc) chegam pela mesma conexão —
// por isso o listener usa socket.onAny filtrando prefixo "voice." em vez de
// uma lista fixa. Manter lista hardcoded dropava silenciosamente os eventos
// novos (hook escutava no callback mas o socket nunca os registrava).

export function useVoiceSocket(
  onEvent: (evt: VoiceEvent) => void,
  enabled = true,
) {
  const socketRef = useRef<Socket | null>(null);
  const callbackRef = useRef(onEvent);
  callbackRef.current = onEvent;

  const [connected, setConnected] = useState(false);

  // Guard pra evitar loop infinito de refresh: se o segundo connect_error
  // tb falhar pos-refresh, dropa em vez de tentar de novo. (Mesmo padrao do
  // use-chat-socket.ts — o VoiceGateway emite `auth_required` + disconnect
  // quando jwt.verify falha.)
  const refreshAttemptedRef = useRef(false);

  useEffect(() => {
    // #112: nao abre o socket /voice sem permissao. O gate fica alinhado ao
    // gateway (que exige telefonia.dashboard.ver) — evita N sockets barrados
    // (Voice disconnected user=undefined) de quem nao usa telefonia, x abas x
    // NAT do escritorio. Consumidores sem `enabled` seguem com default true.
    if (!enabled) return;
    const token = api.getAccessToken();
    if (!token) return;

    // Socket.IO conecta na raiz do dominio (Nginx/Traefik faz proxy /socket.io -> API).
    // Fallback em prod sem env: window.location.origin (mesmo origin do browser).
    const apiUrl = process.env.NEXT_PUBLIC_API_URL
      || (typeof window !== "undefined" ? `${window.location.origin}/api` : "/api");
    const wsUrl = apiUrl.replace("/api", "");

    // #178: paridade com use-chat-socket — teto de 5s fazia reconexão eterna
    // a cada poucos segundos sob rede ruim persistente; N agentes atrás do
    // mesmo NAT degradando juntos viravam storm de handshake (jwt.verify +
    // hasPermission) que amplificava CPU da API (135% no pico, Minas 09/07).
    // Backoff exponencial até 30s + jitter dessincroniza os clientes.
    const socket = io(`${wsUrl}/voice`, {
      auth: { token },
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      randomizationFactor: 0.5,
    });

    // Refs nomeadas pros listeners — sem isso o cleanup só fazia offAny() e
    // disconnect, mas connect/disconnect ficavam pendurados. io() é singleton
    // por namespace: navegações/remounts (Strict Mode dobra) iam empilhando
    // listeners no MESMO socket, causando setState múltiplo e closure stale.
    const onConnect = () => {
      setConnected(true);
      // Conexão nova estabelecida — libera o guard pro próximo ciclo de
      // expiração do token (paridade use-chat-socket).
      refreshAttemptedRef.current = false;
    };
    const onDisconnect = () => setConnected(false);
    const onAnyHandler = (event: string, data: Record<string, unknown>) => {
      if (event.startsWith("voice.")) {
        callbackRef.current({ event, data });
      }
    };

    // Refresh JWT on auth_required (autoritativo) ou connect_error com token
    // visivelmente expirado (defensivo) — espelho do use-chat-socket.ts.
    const doRefreshAndReconnect = (reason: string) => {
      if (refreshAttemptedRef.current) {
        console.warn(`[voice-socket] refresh ja tentado (reason=${reason})`);
        return;
      }
      refreshAttemptedRef.current = true;
      api
        .refreshTokens()
        .then((ok) => {
          if (!ok) {
            // refreshTokens ja chamou clearTokens + redirect /login.
            return;
          }
          const newToken = api.getAccessToken();
          if (!newToken) return;
          socket.auth = { token: newToken };
          socket.disconnect();
          socket.connect();
        })
        .catch((refreshErr) => {
          console.warn(
            `[voice-socket] refresh falhou (reason=${reason}):`,
            refreshErr,
          );
        });
    };

    const onAuthRequired = (payload: { reason?: string }) => {
      doRefreshAndReconnect(payload?.reason ?? "server_auth_required");
    };

    const onConnectError = (err: Error) => {
      const currentToken = api.getAccessToken();
      const expired = isTokenExpired(currentToken);
      if (!expired) {
        console.warn("[voice-socket] connect_error", err?.message ?? err);
        return;
      }
      doRefreshAndReconnect("client_token_expired");
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("auth_required", onAuthRequired);
    socket.on("connect_error", onConnectError);
    socket.onAny(onAnyHandler);

    // #111: token rotacionado atualiza socket.auth do socket VIVO (sem reconectar)
    // — espelho do use-chat-socket; reconexao organica parte de token fresco.
    const unsubTokenRefresh = api.onTokenRefreshed((newToken) => {
      socket.auth = { token: newToken };
    });

    socketRef.current = socket;

    return () => {
      unsubTokenRefresh();
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("auth_required", onAuthRequired);
      socket.off("connect_error", onConnectError);
      socket.offAny(onAnyHandler);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [enabled]);

  return { connected, socket: socketRef.current };
}
